// Composition helper mirroring how the iOS AppEnvironment builds the weather stack (ARCHITECTURE.md §4.4): MET Norway
// through the PWA's same-origin Worker route, the persisted response store and forecast cache, and the connectivity
// monitor. Demo mode uses DemoWeatherProvider with an in-memory cache, so synthetic forecasts never mix with real
// ones in IndexedDB (iOS keeps them in a separate `WeatherDemo/` folder).

import type { EpochMs } from '../core/models';
import { memoryStore } from '../storage/idb';
import type { ConnectivityProviding } from '../net/connectivity';
import { FetchHTTPClient } from '../net/fetchHTTPClient';
import type { HTTPClient } from '../net/httpTypes';
import { DemoWeatherProvider, type DemoWeatherScenario } from './demoWeatherProvider';
import { METNorwayResponseStore } from './metNorwayResponseStore';
import { METNorwayWeatherProvider } from './metNorwayWeatherProvider';
import { WeatherCache } from './weatherCache';
import { WeatherService } from './weatherService';

export interface WeatherStackOptions {
  /** The app's ConnectivityMonitor (its `forceOffline` is the manual offline mode). */
  connectivity: ConnectivityProviding;
  /** Defaults to a FetchHTTPClient using `connectivity`. */
  client?: HTTPClient;
  /** Same-origin route the Worker forwards to the relay (default `/api/weather/locationforecast/2.0/complete`). */
  endpoint?: string;
  /** Synthetic forecasts (demo mode, UI tests); never persisted. */
  demoScenario?: DemoWeatherScenario;
  /** App clock (demo time in demo mode). */
  now?: () => EpochMs;
}

export function makeWeatherService(options: WeatherStackOptions): WeatherService {
  const now = options.now ?? Date.now;
  if (options.demoScenario) {
    return new WeatherService({
      provider: new DemoWeatherProvider({ now, scenario: options.demoScenario }),
      cache: new WeatherCache(memoryStore()),
      connectivity: options.connectivity,
      now,
    });
  }
  const client = options.client ?? new FetchHTTPClient({ connectivity: options.connectivity });
  return new WeatherService({
    provider: new METNorwayWeatherProvider({
      client,
      responseStore: new METNorwayResponseStore(),
      endpoint: options.endpoint,
      now,
    }),
    cache: new WeatherCache(),
    connectivity: options.connectivity,
    now,
  });
}
